import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Text, Textarea, Input, VStack, HStack, Image,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, SliderMark,
} from '@chakra-ui/react';

const TEXT_KEY = 'tesseractWelcomeText';
const IMAGE_KEY = 'tesseractWelcomeImage';
const FONT_SIZE_KEY = 'tesseractWelcomeFontSize';
const DEFAULT_MESSAGES = [
  '*Zoom* OUT',
  'Tesseract *\u20BF*',
  '*WAGMI*',
  '*HFSP*',
  '*Welcome*',
  "Don't Get *REKT*",
  '*Check* Your Security',
  '*DYOR*',
  '*Enter* Tesseract',
];

const getDefaultText = () =>
  DEFAULT_MESSAGES[Math.floor(Math.random() * DEFAULT_MESSAGES.length)];
const DEFAULT_FONT_SIZE = 33;
const MIN_FONT = 14;
const MAX_FONT = 72;
const IMAGE_BASE_W = 300;
const IMAGE_CAP_W = 640;
const MAX_LEN = 200;
const MAX_URL_LEN = 500;
const ORANGE = '#F7931A';

const sanitize = (text) =>
  text.replace(/<[^>]*>/g, '').replace(/[<>]/g, '').trim().slice(0, MAX_LEN);

const sanitizeUrl = (url) => {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  try {
    const u = new URL(trimmed);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return trimmed.slice(0, MAX_URL_LEN);
  } catch {
    return '';
  }
};

const FORMAT_RE = /(\*[^*\n]+\*)/g;

const renderFormatted = (text) => {
  const parts = text.split(FORMAT_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.length >= 3 && part.startsWith('*') && part.endsWith('*')) {
      return <Text as="span" key={i} color={ORANGE} fontWeight="bold">{part.slice(1, -1)}</Text>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const loadInitialText = () => {
  try {
    const saved = localStorage.getItem(TEXT_KEY);
    if (saved === null) return getDefaultText();
    return sanitize(saved);
  } catch {
    return getDefaultText();
  }
};

const loadInitialImage = () => {
  try {
    const saved = localStorage.getItem(IMAGE_KEY);
    if (saved) return sanitizeUrl(saved);
  } catch {
    // ignore
  }
  return '';
};

const loadInitialFontSize = () => {
  try {
    const saved = localStorage.getItem(FONT_SIZE_KEY);
    if (saved !== null) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n >= MIN_FONT && n <= MAX_FONT) return n;
    }
  } catch {
    // ignore
  }
  return DEFAULT_FONT_SIZE;
};

const HIDDEN_AT_KEY = 'tesseractWelcomeHiddenAt';
const RESET_THRESHOLD_MS = 2 * 60 * 1000;

export const resetWelcomeDefaults = () => {
  try {
    localStorage.removeItem(TEXT_KEY);
    localStorage.removeItem(IMAGE_KEY);
    localStorage.removeItem(FONT_SIZE_KEY);
  } catch {
    // ignore
  }
};

// Toggle handler with 2-min debounced reset. Call with current visibility;
// returns the new visibility. On hide, records the timestamp. On show, if it
// was hidden for longer than RESET_THRESHOLD_MS, resets stored preferences
// so the component remounts with defaults.
export const toggleWelcomeWithDebounce = (currentlyVisible) => {
  try {
    if (currentlyVisible) {
      localStorage.setItem(HIDDEN_AT_KEY, String(Date.now()));
    } else {
      const raw = localStorage.getItem(HIDDEN_AT_KEY);
      const hiddenAt = raw ? parseInt(raw, 10) : 0;
      if (hiddenAt && Date.now() - hiddenAt > RESET_THRESHOLD_MS) {
        resetWelcomeDefaults();
      }
      localStorage.removeItem(HIDDEN_AT_KEY);
    }
  } catch {
    // ignore
  }
  return !currentlyVisible;
};

export const EditableWelcome = React.memo(function EditableWelcome() {
  const [text, setText] = useState(loadInitialText);
  const [imageUrl, setImageUrl] = useState(loadInitialImage);
  const [fontSize, setFontSize] = useState(loadInitialFontSize);
  const [imageFailed, setImageFailed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [editImage, setEditImage] = useState('');
  const [editFont, setEditFont] = useState(DEFAULT_FONT_SIZE);
  const textareaRef = useRef(null);

  const handleSave = useCallback(() => {
    const cleanedText = sanitize(editValue);
    const cleanedUrl = sanitizeUrl(editImage);
    const cleanedFont = Math.max(MIN_FONT, Math.min(MAX_FONT, editFont || DEFAULT_FONT_SIZE));
    setText(cleanedText);
    setImageUrl(cleanedUrl);
    setFontSize(cleanedFont);
    setImageFailed(false);
    try {
      localStorage.setItem(TEXT_KEY, cleanedText);
      if (cleanedUrl) localStorage.setItem(IMAGE_KEY, cleanedUrl);
      else localStorage.removeItem(IMAGE_KEY);
      localStorage.setItem(FONT_SIZE_KEY, String(cleanedFont));
    } catch {
      // ignore storage errors
    }
    setIsEditing(false);
  }, [editValue, editImage, editFont]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
      setEditValue('');
      setEditImage('');
    }
  }, [handleSave]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setEditValue(text);
    setEditImage(imageUrl);
    setEditFont(fontSize);
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <Box
        maxW="380px"
        pointerEvents="auto"
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) handleSave();
        }}
      >
        <VStack align="stretch" spacing={1}>
          <Input
            value={editImage}
            onChange={(e) => setEditImage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Image URL (optional — https://…)"
            fontSize="12px"
            fontFamily="mono"
            color="white"
            bg="rgba(0,0,0,0.6)"
            border="1px solid"
            borderColor={ORANGE}
            _focus={{ borderColor: ORANGE, boxShadow: `0 0 0 1px ${ORANGE}` }}
            _placeholder={{ color: 'whiteAlpha.600' }}
            maxLength={MAX_URL_LEN}
          />
          <Textarea
            ref={textareaRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            fontSize={`${editFont}px`}
            fontFamily="mono"
            color="white"
            bg="rgba(0,0,0,0.6)"
            border="1px solid"
            borderColor={ORANGE}
            _focus={{ borderColor: ORANGE, boxShadow: `0 0 0 1px ${ORANGE}` }}
            placeholder="Your message…"
            maxLength={MAX_LEN}
            rows={3}
            resize="none"
          />
          <HStack spacing={2} align="center" w="full">
            <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.15em" minW="28px">
              SIZE
            </Text>
            <Slider
              flex="1"
              min={MIN_FONT}
              max={MAX_FONT}
              step={1}
              value={editFont}
              onChange={setEditFont}
              focusThumbOnChange={false}
            >
              <SliderMark
                value={DEFAULT_FONT_SIZE}
                mt="-4px"
                w="1px"
                h="10px"
                bg="whiteAlpha.700"
                pointerEvents="none"
              />
              <SliderTrack bg="whiteAlpha.200">
                <SliderFilledTrack bg={ORANGE} />
              </SliderTrack>
              <SliderThumb boxSize={3} bg={ORANGE} />
            </Slider>
            <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.700" minW="34px" textAlign="right">
              {Math.round((editFont / DEFAULT_FONT_SIZE) * 100)}%
            </Text>
          </HStack>
          <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.15em">
            ENTER SAVE · ESC CANCEL · *WORD* FOR ORANGE
          </Text>
        </VStack>
      </Box>
    );
  }

  const showImage = imageUrl && !imageFailed;
  const isEmpty = !text && !showImage;
  const imageMaxW = Math.min(Math.round(IMAGE_BASE_W * (fontSize / DEFAULT_FONT_SIZE)), IMAGE_CAP_W);
  const containerMaxW = Math.max(380, imageMaxW);

  return (
    <Box
      maxW={`${containerMaxW}px`}
      pointerEvents="auto"
      cursor="pointer"
      onClick={handleClick}
      _hover={{ opacity: 0.8 }}
      title="Click to edit welcome message"
      userSelect="none"
      transition="opacity 0.2s"
    >
      <VStack align="flex-start" spacing={2}>
        {showImage && (
          <Image
            src={imageUrl}
            alt=""
            maxW={`${imageMaxW}px`}
            h="auto"
            onError={() => setImageFailed(true)}
            draggable={false}
            filter="drop-shadow(0 0 8px rgba(0,0,0,0.8))"
          />
        )}
        {text && (
          <Text
            fontSize={`${fontSize}px`}
            fontFamily="mono"
            color="white"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            lineHeight="1.1"
            textShadow="0 0 12px rgba(0,0,0,0.8), 0 0 4px rgba(0,0,0,0.9)"
          >
            {renderFormatted(text)}
          </Text>
        )}
        {isEmpty && (
          <Text
            fontSize="12px"
            fontFamily="mono"
            color="whiteAlpha.600"
            letterSpacing="0.15em"
            fontStyle="italic"
          >
            (click to edit)
          </Text>
        )}
      </VStack>
    </Box>
  );
});
